(() => {
  'use strict';

  if (window.__seoAttributionV1Loaded || window.Shopify?.designMode) return;
  window.__seoAttributionV1Loaded = true;

  const script = document.currentScript;
  const store = (script?.dataset.store || window.location.hostname).replace(/[^a-z0-9]+/gi, '_');
  const storageKey = `seo_attribution_v1_${store}`;
  const syncedKey = `${storageKey}_synced`;
  const maxAge = 90 * 24 * 60 * 60 * 1000;

  const clean = (value, limit = 240) => String(value || '').trim().slice(0, limit);
  const root = () => window.Shopify?.routes?.root || '/';

  function sessionValue(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, value);
    } catch (_error) {
      return null;
    }
  }

  function analyticsAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return true;
    return privacy.analyticsProcessingAllowed();
  }

  function readSaved() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
      if (!saved?.first?.at || Date.now() - Date.parse(saved.first.at) > maxAge) return null;
      return saved;
    } catch (_error) {
      return null;
    }
  }

  function classifyTouch() {
    const params = new URLSearchParams(window.location.search);
    const referrer = document.referrer ? new URL(document.referrer) : null;
    const referrerDomain = clean(referrer?.hostname.replace(/^www\./, '').toLowerCase());
    const utmSource = clean(params.get('utm_source')).toLowerCase();
    const utmMedium = clean(params.get('utm_medium')).toLowerCase();
    const clickIds = {
      gclid: params.get('gclid'),
      gbraid: params.get('gbraid'),
      wbraid: params.get('wbraid'),
      msclkid: params.get('msclkid'),
      fbclid: params.get('fbclid'),
      ttclid: params.get('ttclid'),
    };
    const clickIdType = Object.keys(clickIds).find((key) => clickIds[key]);

    let source = utmSource;
    let medium = utmMedium;

    if (!source && ['gclid', 'gbraid', 'wbraid'].includes(clickIdType)) {
      source = 'google';
      medium = 'cpc';
    } else if (!source && clickIdType === 'msclkid') {
      source = 'bing';
      medium = 'cpc';
    } else if (!source && clickIdType === 'fbclid') {
      source = 'meta';
      medium = 'paid-social';
    } else if (!source && clickIdType === 'ttclid') {
      source = 'tiktok';
      medium = 'paid-social';
    } else if (!source && /(^|\.)google\./.test(referrerDomain)) {
      source = 'google';
      medium = 'organic';
    } else if (!source && /(^|\.)(bing\.com|yahoo\.|duckduckgo\.com)/.test(referrerDomain)) {
      source = referrerDomain.includes('bing')
        ? 'bing'
        : referrerDomain.includes('yahoo')
          ? 'yahoo'
          : 'duckduckgo';
      medium = 'organic';
    } else if (!source && referrerDomain && referrerDomain !== window.location.hostname.replace(/^www\./, '')) {
      source = referrerDomain;
      medium = 'referral';
    } else if (!source) {
      source = 'direct';
      medium = 'none';
    }

    return {
      source: clean(source || 'direct'),
      medium: clean(medium || 'none'),
      campaign: clean(params.get('utm_campaign')),
      term: clean(params.get('utm_term')),
      content: clean(params.get('utm_content')),
      click_id_type: clean(clickIdType),
      click_id: clean(clickIdType ? clickIds[clickIdType] : ''),
      referrer_domain: referrerDomain,
      landing_page: clean(window.location.pathname, 300),
      at: new Date().toISOString(),
    };
  }

  function buildState() {
    const current = classifyTouch();
    const saved = readSaved();
    const first = saved?.first || current;
    const last = current.source === 'direct' && saved?.last ? saved.last : current;
    const state = { first, last };

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (_error) {
      // The cart copy below still preserves attribution when storage is unavailable.
    }

    return state;
  }

  function cartAttributes(state) {
    const attributes = { __seo_measurement_version: '2026-08-30-v1' };

    for (const [touchName, touch] of Object.entries(state)) {
      for (const [key, value] of Object.entries(touch)) {
        if (value) attributes[`__seo_${touchName}_${key}`] = clean(value, 300);
      }
    }

    return attributes;
  }

  async function syncAttribution(force = false) {
    if (!analyticsAllowed()) return;

    const state = buildState();
    const signature = JSON.stringify(state);
    if (!force && sessionValue(syncedKey) === signature) return;

    try {
      const response = await window.fetch(`${root()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes: cartAttributes(state) }),
      });

      if (response.ok) sessionValue(syncedKey, signature);
    } catch (_error) {
      // Attribution is supplementary and must never block storefront behavior.
    }
  }

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    if (/\/cart\/add(?:\.js)?(?:\?|$)/.test(requestUrl)) {
      window.setTimeout(() => syncAttribution(true), 0);
    }
    return response;
  };

  syncAttribution();
  document.addEventListener('visitorConsentCollected', () => syncAttribution(true));
  window.addEventListener('pageshow', () => syncAttribution());
})();
